const Handlebars = require('handlebars/runtime');

// regex to match a string of the format {{method arg1 arg2 arg3}}
// the first matching group will be the method, and the second will be the list of args
const mainRegex = /\{\{(\w+) ([^{}]+)\}\}/g;

const allowedIncentiveClasses = new Set(['DiscountPercent', 'DiscountAmount', 'ShippingDiscountPercent']);

const prepaidAttributeMap = {
  TotalPrice: 'total-price',
  PerDeliveryPrice: 'per-delivery-price',
  TotalSavings: 'total-savings',
  PerDeliverySavings: 'per-delivery-savings',
  PercentageSavings: 'percentage-savings',
  ExtraPercentageSavings: 'extra-percentage-savings',
  NumberOfShipments: 'number-of-shipments'
};

const allowedPrepaidClasses = new Set(Object.keys(prepaidAttributeMap));

const priceAttributeMap = {
  Regular: 'regular',
  Subscription: 'subscription',
  Discount: 'discount',
  PayAsYouGo: 'pay-as-you-go'
};

const allowedPriceClasses = new Set(Object.keys(priceAttributeMap));

const INCENTIVE_TYPE_INITIAL = 'initial';
const INCENTIVE_TYPE_ONGOING = 'ongoing';
const allowedIncentiveTypes = new Set([INCENTIVE_TYPE_INITIAL, INCENTIVE_TYPE_ONGOING]);

const isInt = str => !Number.isNaN(parseInt(str, 10));
const parseIncentiveType = (arg2, arg3) => (arg2 && !isInt(arg2) ? arg2 : arg3 || INCENTIVE_TYPE_ONGOING);

/**
 * An object that can handle a given regex replacement.
 * @typedef {import('./types').MethodHandler} MethodHandler
 */

/**
 * @type {MethodHandler}
 * Handles matches of the format {{ogIncentive DiscountClass value? incentiveType? }}
 */
const ogIncentiveMethodHandler = {
  method: 'ogIncentive',
  argsRegex: /(\w+)(?:\s(\w+))?(?:\s(\w+))?/,
  shouldHandleResult: args => {
    const [incentiveClass, arg2, arg3] = args;
    if (!allowedIncentiveClasses.has(incentiveClass)) {
      return false;
    }

    if (arg2 && arg3 && ((isInt(arg2) && isInt(arg3)) || (!isInt(arg2) && !isInt(arg3)))) {
      return false;
    }

    const incentiveType = parseIncentiveType(arg2, arg3);

    if (incentiveType && !allowedIncentiveTypes.has(incentiveType)) {
      return false;
    }

    return true;
  },
  handleResult: args => {
    const [incentiveClass, arg2, arg3] = args;

    const incentiveValue = arg2 && isInt(arg2) ? arg2 : arg3 || undefined;
    const incentiveType = parseIncentiveType(arg2, arg3);

    let textWithIncentives = `<og-incentive-text from="${incentiveClass}"`;
    if (typeof incentiveValue !== 'undefined') textWithIncentives += ` value="${incentiveValue}"`;
    if (incentiveType === INCENTIVE_TYPE_INITIAL) textWithIncentives += ` initial`;
    textWithIncentives += `></og-incentive-text>`;

    return textWithIncentives;
  }
};

/**
 * @type {MethodHandler}
 * Handles matches of the format {{ogPrepaid PrepaidClass}}
 */
const ogPrepaidMethodHandler = {
  method: 'ogPrepaid',
  argsRegex: /(\w+)/,
  shouldHandleResult: args => {
    const [prepaidClass] = args;
    return allowedPrepaidClasses.has(prepaidClass);
  },
  handleResult: args => {
    const [prepaidClass] = args;
    return `<og-prepaid-data ${prepaidAttributeMap[prepaidClass]}></og-prepaid-data>`;
  }
};

/**
 * @type {MethodHandler}
 * Handles matches of the format {{ogPrice PriceClass}}
 */
const ogPriceHandler = {
  method: 'ogPrice',
  argsRegex: /(\w+)/,
  shouldHandleResult: args => {
    const [priceType] = args;
    return allowedPriceClasses.has(priceType);
  },
  handleResult: args => {
    const [priceType] = args;
    return `<og-price ${priceAttributeMap[priceType]}></og-price>`;
  }
};

const methodHandlers = [ogPrepaidMethodHandler, ogIncentiveMethodHandler, ogPriceHandler];

function withIncentives(text) {
  if (!text) return text;
  let textWithIncentives = '';
  let result = mainRegex.exec(text);
  let lastIndex = 0;
  while (result !== null) {
    const [match, method, args] = result;
    const methodHandler = methodHandlers.find(handler => handler.method === method);
    const parsedArgs = methodHandler?.argsRegex.exec(args)?.slice(1);
    if (parsedArgs && methodHandler.shouldHandleResult(parsedArgs)) {
      textWithIncentives += Handlebars.escapeExpression(text.substring(lastIndex, mainRegex.lastIndex - match.length));
      textWithIncentives += methodHandler.handleResult(parsedArgs);
    } else {
      textWithIncentives += Handlebars.escapeExpression(text.substring(lastIndex, mainRegex.lastIndex));
    }
    lastIndex = mainRegex.lastIndex;

    result = mainRegex.exec(text);
  }
  textWithIncentives += Handlebars.escapeExpression(text.substring(lastIndex, text.length));
  return new Handlebars.SafeString(textWithIncentives);
}

module.exports = withIncentives;
